import React, { useState, forwardRef  } from "react";
import Select from "react-select";
import FloatingLabelSelectStyle from"./FloatingLabelSelect.module.scss";
import { BsBorderWidth } from "react-icons/bs";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";

const FloatingLabelSelect = forwardRef (({
	id,
	label,
	value, 
	onChange,
	options,
	isInvalid = false,
	placeholder = "Select...",
	selectClassName = "",
	isDisabled = false,
	isClearable = false,
	inputHighlight = false,
	error,
	touched,
	leftIcon = null,
	rightIcon = null,
	showErrorMessage = true,
	inputType = "reactSelect",
	asyncSearchOptions,
	setSelectedOption,
	selectedOption,
	handleCreate,
	...props
}, ref) => {
	const [isFocused, setIsFocused] = useState(false);
	const hasValue = ((value?.value !== null && value?.value !== undefined) && typeof value?.value !== "undefined" && value?.value !== "") || ((props.inputValue !== null && props.inputValue !== undefined) && typeof props.inputValue !== "undefined" && props.inputValue !== "");
	const effectiveTouched = touched ?? false;
	const showError = Boolean(error) && (effectiveTouched || isInvalid);
	const invalidState = isInvalid || showError;
	const hasRenderableIcon = icon => icon !== null && icon !== undefined && icon !== "" && icon !== false;
	const hasLeftIcon = hasRenderableIcon(leftIcon);
	const hasRightIcon = hasRenderableIcon(rightIcon);
	const { onBlur: externalOnBlur, onFocus: externalOnFocus, onMenuClose: externalOnMenuClose, ...restProps } = props;
	const customReactSelectStyles = {
		control: (provided, state) => ({
			...provided,
			minHeight: "48px",
			height: "48px",
			paddingLeft: hasLeftIcon ? "2rem" : "0.5rem",
			paddingRight: hasRightIcon ? "2rem" : "0",
			backgroundColor: isDisabled && !hasRightIcon ? "#f8f8f8" : "#f8fafc",
			border: `1px solid ${hasValue && !invalidState ? "#00a9e0" : invalidState ? "#FF0000" : "#d4d4d4" }`,
			borderRadius: "8px",
			// fontFamily: '"Montserrat", sans-serif',
			fontSize: "14px",
			borderWidth: "1.5px",
			boxShadow:"none",
			"&:hover": {
				borderColor: hasValue && !invalidState ? "#00a9e0" : invalidState ? "#FF0000" : "#d4d4d4",
			},
			
		}),
		valueContainer: provided => ({
			...provided,
			paddingLeft: "8px",
			overflow: "visible"
		}),
		placeholder: provided => ({
			...provided,
			color: "#999999",
			marginLeft: "2px",
			marginRight: "2px",
			fontSize: "14px",
			fontWeight: 400,
		}),
		singleValue: (provided, state) => {
			const opacity = 1;
			const transition = "opacity 300ms";
			const style = {
				...provided,
				borderStyle: "none",
				fontWeight: 400,
				color: !isDisabled ? "#333333" : "#999999",
				marginLeft: "0",
				marginRight: "2px",
				paddingLeft: "0",
				fontSize: "14px",
			};

			return { ...style, opacity, transition };
		},
		input: provided => ({
			...provided,
			color: "#333333",
			margin: "0 2px",
			padding: "0",
		}),
		indicatorSeparator: (provided, state) => ({
			...provided,
			visibility: "hidden",
		}),
		dropdownIndicator: (provided, state) => ({
			...provided,
			padding: "0.5rem",
			color: "#666666",
			transition: "color 0.15s ease-in-out",
			"&:hover": {
				color: "#333333",
			},
			display: isDisabled ? "none" : "block",
		}),
		menu: provided => ({
			...provided,
			// fontFamily: '"Montserrat", sans-serif',
			borderRadius: "8px",
			marginTop: "4px",
			zIndex: 10000,
			border: "1px solid #c4c4c4",
			boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
		}),
		menuPortal: provided => ({
			...provided,
			zIndex: 10000,
		}),
		option: (provided, state) => ({
			...provided,
			backgroundColor: state.isSelected ? "#f0f0f0" : state.isFocused ? "#f8f8f8" : "white",
			color: state.isSelected ? "#333333" : "#333333",
			padding: "0.5rem 0.75rem",
			"&:active": {
				backgroundColor: "#e8e8e8",
			},
			fontSize: "14px",
			fontWeight: "normal",
			zIndex: 10000,
		}),
	};
 
	const WrapperComponent = inputType === "asyncSelect" ? AsyncSelect : 
		inputType === "AsyncCreatableSelect" ? AsyncCreatableSelect :
		inputType === "reactSelect" && Select ;
 
	return (
		<div
			className={`${FloatingLabelSelectStyle.outlinedSelectContainer} ${FloatingLabelSelectStyle.customFormField} ${invalidState ? `${FloatingLabelSelectStyle.isInvalid}` : ""} ${isDisabled && !hasRightIcon ? `${FloatingLabelSelectStyle.isDisabled}` : ""}
			${isFocused ? `${FloatingLabelSelectStyle.isFocused}` : "" } ${hasLeftIcon ? `${FloatingLabelSelectStyle.hasLeftIcon}` : ""}`}
		>
			<label htmlFor={id} className={`${FloatingLabelSelectStyle.outlinedLabel} ${isFocused ? `${FloatingLabelSelectStyle.isFocused}` : "" } ${hasLeftIcon ? `${FloatingLabelSelectStyle.labelWithLeftIcon}` : ""}`} style={{ color: isDisabled ? "#999999" : "#006485", background: "linear-gradient(0deg,rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 39%, rgba(255, 255, 255, 1) 40%, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 0) 54%)" }}>
				{label}
			</label>
			{hasLeftIcon && (
				<span className={FloatingLabelSelectStyle.leftIcon}>
					{leftIcon}
				</span>
			)}

			<WrapperComponent
				inputId={id}
				value={value}
				ref={ref}
				onChange={onChange}
				options={options}
				styles={customReactSelectStyles}
				className={`floatingLabelSelect ${!error && hasValue ? `${FloatingLabelSelectStyle.hasValue}` : ""} ${FloatingLabelSelectStyle.reactSelectInstance} ${FloatingLabelSelectStyle[`${selectClassName}`]}`}
				classNamePrefix="react-select"
				placeholder={placeholder}
				isDisabled={isDisabled}
				//	isClearable={isClearable}
				onFocus={(event) => {
					setIsFocused(true);
					if (typeof externalOnFocus === 'function') {
						externalOnFocus(event);
					}
				}}
				onMenuClose={(...args) => {
					setIsFocused(false);
					if (typeof externalOnMenuClose === 'function') {
						externalOnMenuClose(...args);
					}
				}}
				onBlur={(event) => {
					setIsFocused(false);
					if (typeof externalOnBlur === 'function') {
						externalOnBlur(event);
					}
				}}
				// menuPlacement="auto"
				// menuPosition="fixed"
				// menuPortalTarget={document.body}

				{...(inputType === "asyncSelect" ? {  
					cacheOptions: true,
					defaultOptions: true, 
					loadOptions: asyncSearchOptions, 
					onChange: onChange,
					value: value
				} : {})} 

				{...(inputType === "AsyncCreatableSelect" ? {  
					cacheOptions: true,
					defaultOptions: true, 
					loadOptions: asyncSearchOptions, 
					onChange: onChange,
					isClearable: true,
					onCreateOption: handleCreate,
					formatCreateLabel: (inputValue) => {
						return <span dangerouslySetInnerHTML={{ __html: `use <strong>${inputValue}</strong> as location`}} />
					},
					value: value
				} : {})}

				{...restProps}
			/>
			{hasRightIcon && (
				<span className={FloatingLabelSelectStyle.rightIcon}>
					{rightIcon}
				</span>
			)}
			<div className={FloatingLabelSelectStyle.errorText} aria-live="polite">
				{(showError && showErrorMessage) && <span>{error}</span>}
			</div>
		</div>
	);
});

export default FloatingLabelSelect;
